import React, { useState, useEffect, useTransition } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  AlertTriangle, 
  AlertCircle, 
  ChevronDown, 
  ChevronUp, 
  X, 
  ExternalLink, 
  Clock, 
  ArrowRight,
  Cpu,
  Mail,
  Database
} from 'lucide-react';
import { fetchQuotaSummary } from '@/services/quotaService';
import { QuotaAlertSummary, ActiveQuotaAlert } from '@/types/quota';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

const DISMISS_STORAGE_KEY = 'five_lens_quota_alert_dismissal';

interface DismissalState {
  level: 'warning' | 'critical';
  dismissedAt: number;
}

export function ApiQuotaAlertBanner() {
  const { user } = useAdminAuth();
  const location = useLocation();
  const [data, setData] = useState<QuotaAlertSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [, startTransition] = useTransition();

  const loadQuotaData = async () => {
    try {
      const token = user ? await user.getIdToken() : null;
      const summary = await fetchQuotaSummary(token);
      startTransition(() => {
        setData(summary);
      });
      
      // Check dismissal validity (24 hours or level change)
      if (summary) {
        try {
          const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
          if (raw) {
            const state: DismissalState = JSON.parse(raw);
            const now = Date.now();
            const hoursElapsed = (now - state.dismissedAt) / (3600 * 1000);
            const currentLevel = summary.overallStatus === 'critical' ? 'critical' : 'warning';

            if (hoursElapsed < 24 && state.level === currentLevel) {
              setDismissed(true);
            } else {
              localStorage.removeItem(DISMISS_STORAGE_KEY);
              setDismissed(false);
            }
          }
        } catch {}
      }
    } catch (e) {
      console.warn('Quota banner update notice:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotaData();
    const interval = setInterval(() => {
      loadQuotaData();
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Show full banner ONLY on /admin and /admin/quotas
  const isOverviewOrQuotasPage = location.pathname === '/admin' || location.pathname === '/admin/quotas';
  if (!isOverviewOrQuotasPage || !data) return null;

  const hasWarnings = data.activeAlerts.length > 0;
  const isCritical = data.overallStatus === 'critical';
  const highest = data.highestPercent;
  const primaryAlert: ActiveQuotaAlert | undefined = data.activeAlerts[0];

  if (dismissed && hasWarnings) {
    return (
      <div className="bg-amber-500 text-ink text-xs py-1.5 px-4 border-b border-amber-600 flex items-center justify-between z-50 relative shadow-sm">
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="w-3.5 h-3.5 text-ink shrink-0" />
          <span>
            API Quota Warning Snoozed ({data.activeAlerts.length} approaching limit, peak at {highest}%).
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => {
              try {
                localStorage.removeItem(DISMISS_STORAGE_KEY);
              } catch {}
              setDismissed(false);
            }} 
            className="underline font-bold hover:text-black text-[11px]"
          >
            Show Alert
          </button>
          <Link to="/admin/quotas" className="bg-ink text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider hover:bg-rule-3 transition-colors">
            Inspect Quotas
          </Link>
        </div>
      </div>
    );
  }

  if (!hasWarnings) return null;

  const handleDismiss = () => {
    try {
      const state: DismissalState = {
        level: isCritical ? 'critical' : 'warning',
        dismissedAt: Date.now()
      };
      localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(state));
    } catch {}
    setDismissed(true);
  };

  // Get accurate calmer wording per service
  const getServiceSpecificWording = (alert: ActiveQuotaAlert) => {
    if (alert.serviceId === 'mailchimp') {
      return `You're at ${alert.current.toLocaleString()} of ${alert.limit.toLocaleString()} contacts on your Mailchimp plan. At the limit, Mailchimp charges for extra contacts or asks you to upgrade. Archiving unsubscribed and cleaned contacts frees space.`;
    }
    if (alert.serviceId === 'resend') {
      return `${alert.current.toLocaleString()} of ${alert.limit.toLocaleString()} ${alert.unit} sent today / this month. At the limit, Resend stops sending until the limit resets.`;
    }
    if (alert.serviceId === 'gemini') {
      return `${alert.current.toLocaleString()} of ${alert.limit.toLocaleString()} AI requests today. At the limit, the chat and AI tools stop responding until tomorrow.`;
    }
    return alert.message;
  };

  return (
    <div 
      className={`border-b transition-all duration-300 relative z-50 ${
        isCritical 
          ? 'bg-gradient-to-r from-rose-900 via-rose-950 to-ink text-white border-rose-500 shadow-lg shadow-rose-950/30' 
          : 'bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-ink border-amber-600 shadow-md'
      }`}
      role="alert"
      aria-live="assertive"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-start sm:items-center gap-3">
          <div className={`p-1.5 rounded-lg shrink-0 ${
            isCritical ? 'bg-rose-500 text-white animate-bounce' : 'bg-ink text-amber-300'
          }`}>
            {isCritical ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                isCritical 
                  ? 'bg-rose-600 text-white border border-rose-400' 
                  : 'bg-ink text-white'
              }`}>
                {isCritical ? 'CRITICAL QUOTA ALERT' : 'PRE-ERROR QUOTA WARNING'}
              </span>
              <span className="font-serif font-bold text-sm sm:text-base">
                {primaryAlert?.serviceName} is at {primaryAlert?.percent}% capacity
              </span>
              <span className={`text-xs px-2 py-0.5 rounded font-mono font-semibold ${
                isCritical ? 'bg-black/40 text-rose-200' : 'bg-black/10 text-ink'
              }`}>
                {primaryAlert?.current !== undefined ? primaryAlert.current.toLocaleString() : '–'} / {primaryAlert?.limit !== undefined && primaryAlert?.limit !== null ? primaryAlert.limit.toLocaleString() : '–'} {primaryAlert?.unit}
              </span>
            </div>

            <p className={`text-xs leading-relaxed ${
              isCritical ? 'text-rose-100' : 'text-ink/85 font-medium'
            }`}>
              {primaryAlert ? getServiceSpecificWording(primaryAlert) : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end md:self-auto shrink-0 pt-1 md:pt-0">
          <Link
            to="/admin/quotas"
            className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm ${
              isCritical
                ? 'bg-white text-rose-950 hover:bg-rose-100'
                : 'bg-ink text-white hover:bg-rule-3'
            }`}
          >
            <span>Inspect All Quotas</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          {primaryAlert?.serviceId === 'mailchimp' ? (
            <a
              href="https://admin.mailchimp.com/audience/"
              target="_blank"
              rel="noopener noreferrer"
              className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1 transition-colors border ${
                isCritical 
                  ? 'border-rose-400/50 hover:bg-rose-800/40 text-white' 
                  : 'border-ink/20 hover:bg-black/10 text-ink'
              }`}
            >
              <span>Mailchimp Audience</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : primaryAlert?.actionUrl && (
            <a
              href={primaryAlert.actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1 transition-colors border ${
                isCritical 
                  ? 'border-rose-400/50 hover:bg-rose-800/40 text-white' 
                  : 'border-ink/20 hover:bg-black/10 text-ink'
              }`}
            >
              <span>{primaryAlert.actionLabel || 'Manage in Console'}</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className={`p-1.5 rounded transition-colors ${
              isCritical ? 'hover:bg-rose-800 text-rose-200' : 'hover:bg-black/10 text-ink'
            }`}
            title={expanded ? 'Collapse details' : 'Expand full breakdown'}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          <button
            onClick={handleDismiss}
            className={`p-1.5 rounded transition-colors ${
              isCritical ? 'hover:bg-rose-800 text-rose-200' : 'hover:bg-black/10 text-ink'
            }`}
            title="Dismiss alert for 24 hours"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className={`border-t px-4 sm:px-6 py-4 transition-all ${
          isCritical ? 'bg-black/50 border-rose-800/80' : 'bg-amber-600/10 border-amber-500/50'
        }`}>
          <div className="max-w-7xl mx-auto space-y-4">
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider">
              <span>All Active API Quota Thresholds ({data.activeAlerts.length} Approaching Limit)</span>
              <span>Next Free-Tier Daily Reset: {new Date(data.nextDailyReset).toLocaleTimeString()} UTC</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.activeAlerts.map((alert) => (
                <div 
                  key={`${alert.serviceId}-${alert.metricId}`}
                  className="bg-white/95 text-ink p-3.5 rounded border border-black/10 shadow-sm flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-bold text-xs flex items-center gap-1.5">
                        {alert.serviceId === 'gemini' && <Cpu className="w-3.5 h-3.5 text-rule-3" />}
                        {alert.serviceId === 'firestore' && <Database className="w-3.5 h-3.5 text-blue-600" />}
                        {alert.serviceId === 'mailchimp' && <Mail className="w-3.5 h-3.5 text-amber-600" />}
                        {alert.serviceName}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        alert.severity === 'critical' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {alert.percent}%
                      </span>
                    </div>

                    <p className="text-[11px] font-semibold text-ink/80 mb-2">
                      {alert.metricName}: {alert.current !== undefined ? alert.current.toLocaleString() : '–'} of {alert.limit !== undefined && alert.limit !== null ? alert.limit.toLocaleString() : '–'} {alert.unit}
                    </p>

                    <div className="w-full bg-ground-deep h-2 rounded-full overflow-hidden mb-2">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          alert.severity === 'critical' ? 'bg-rose-600' : 'bg-amber-500'
                        }`}
                        style={{ width: `${Math.min(100, alert.percent)}%` }}
                      />
                    </div>

                    <p className="text-[11px] text-ink/70 leading-relaxed mb-3">
                      {getServiceSpecificWording(alert)}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-ground-deep/60 flex items-center justify-between text-[10px]">
                    {alert.resetsIn ? (
                      <span className="text-ink/60 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Resets in {alert.resetsIn}
                      </span>
                    ) : (
                      <span className="text-ink/60">Fixed Capacity</span>
                    )}

                    {alert.serviceId === 'mailchimp' ? (
                      <a 
                        href="https://admin.mailchimp.com/audience/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-bold text-rule-3 hover:underline flex items-center gap-1"
                      >
                        Mailchimp Audience <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : alert.actionUrl && (
                      <a 
                        href={alert.actionUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-bold text-rule-3 hover:underline flex items-center gap-1"
                      >
                        Upgrade <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2 text-xs">
              <span className={`font-medium ${isCritical ? 'text-rose-200' : 'text-ink/70'}`}>
                Configured Warning Threshold: {data.warningThreshold}% &bull; Critical Threshold: {data.criticalThreshold}%
              </span>
              <Link 
                to="/admin/quotas"
                className={`font-bold underline flex items-center gap-1 ${
                  isCritical ? 'text-white hover:text-rose-200' : 'text-ink hover:text-black'
                }`}
              >
                <span>Open Advanced Quota Settings &amp; Health Matrix</span>
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiQuotaAlertBanner;
